import { Injectable, OnDestroy } from '@angular/core';
import { LabResultsStorage, LabTestResult, UnitOption } from '../../lab-test-results';
import { ActivatedRoute } from '@angular/router';
import { Observable, ConnectableObservable, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { map, filter, publishReplay } from 'rxjs/operators';
import { chain, LoDashExplicitArrayWrapper, includes } from 'lodash';
import { Site } from '../../sites/site';
import { Subject } from '../../subjects/Subject';
import { SubjectLabTestsDataSource } from '../../subjects/subject-lab-tests-data-source';
import { routeData } from '../../shared/route-helpers';
import { UnitStorage } from '../../user/unit-storage';

@Injectable()
export class SelectedSubjectLabResultsStorage implements SubjectLabTestsDataSource, OnDestroy {
    public readonly showOnlyFlagged = new BehaviorSubject(false);

    private readonly labTestResults$: ConnectableObservable<LabTestResult[]>;
    private connection: Subscription;

    constructor(
        labResultsStorage: LabResultsStorage,
        route: ActivatedRoute,
        unitStorage: UnitStorage,
    ) {
        const site = route.parent.parent.data
            .pipe(map(x => x[routeData.selectedSite] as Site));

        const ctSubjectId = route.data
            .pipe(
                map(x => x['selectedSubject'] as Subject | undefined),
                filter(x => !!x),
                map(x => x.ctSubjectId)
            );

        this.labTestResults$ = combineLatest([
            this.showOnlyFlagged,
            unitStorage.currentUnit,
            labResultsStorage.getSubjectLabResults(site, ctSubjectId),
        ])
            .pipe(
                map((data: [boolean, UnitOption, LabTestResult[]]) => {
                    const [onlyFlagged, unitOption, res] = data;
                    let result: LabTestResult[] = null; // show spinner
                    if (res) {
                        let resultChain = chain(res);

                        if (onlyFlagged) {
                            resultChain = this.filterFlaggedTests(resultChain);
                        }

                        result = resultChain.map(r => {
                            r.showResultsInSI = unitOption.isSiUnit;
                            return r;
                        }).value();
                    }

                    return result;
                }),
                publishReplay(1)
            ) as ConnectableObservable<LabTestResult[]>;

        this.connection = this.labTestResults$.connect();
    }

    public get labTestResults(): Observable<LabTestResult[]> {
        return this.labTestResults$;
    }

    public ngOnDestroy() {
        this.connection.unsubscribe();
    }

    private filterFlaggedTests(resChain: LoDashExplicitArrayWrapper<LabTestResult>): LoDashExplicitArrayWrapper<LabTestResult> {
        const flaggedUniqLabTestsKeys = resChain
            .filter(labTestResult => labTestResult.isFlagged)
            .map(labTestResult => labTestResult.uniqLabTestIdKey)
            .uniq()
            .value();

        return resChain.filter(labTestResult => includes(flaggedUniqLabTestsKeys, labTestResult.uniqLabTestIdKey));
    }
}
